import { useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useQuery } from '@apollo/react-hooks'
import { pathOr } from 'ramda'

import {
  toggleAccountNavVisibility,
  setAccountNavVisibility,
  logoutAndRedirectTo,
  switchProfileWithPin,
  switchProfile,
  toggleNavigationVisibility
} from '../../../actions'
import { requirePinForSwitchProfile } from '../../../lib/account'
import { findConfigValue } from '../../../lib/config'
import { hasHigherParentalRating } from '../../../lib/utils'
import { HELP_SITE } from '../../../constants'
import ACCOUNT_QUERY from '../../../../graphql/queries/account.gql'
import MY_RENTALS_QUERY from '../../../../graphql/queries/my-rentals.gql'
import { segmentTrackSignout } from '../../../segment/segment-track'
import { useConfig } from '../../../hooks/use-config'
import { useIsAuthenticated } from '../../../hooks/use-is-authenticated'

/**
 * useNavigationAccount is a custom hook that encapsulates all logic
 * for the top-right account navigation menu in both the navigation right and navigation side.
 * It handles fetching account data and My Rentals,
 * and provides handlers for profile switching, logout, and menu toggling.
 */
export const useNavigationAccount = () => {
  const dispatch = useDispatch()
  const { appConfig } = useConfig()
  const isAuthenticated = useIsAuthenticated()

  // Redux state selectors
  const display = useSelector((state) => state.navigation.accountNav)
  const theme = useSelector((state) => state.theme)
  const navComponentWidth = useSelector((state) => state.navigation.navComponentWidth)

  // Local state
  const [selectedProfile, setSelectedProfile] = useState(null)

  // GraphQL queries
  const { data: accountData, loading: accountLoading } = useQuery(ACCOUNT_QUERY, {
    skip: !isAuthenticated,
    notifyOnNetworkStatusChange: true
  })

  const { data: myRentalsData, loading: myRentalsLoading } = useQuery(MY_RENTALS_QUERY, {
    skip: !isAuthenticated,
    notifyOnNetworkStatusChange: true
  })

  // Derived data from accountData and myRentalsData
  const requirePin = accountData && requirePinForSwitchProfile(accountData.account)
  const currentProfile = accountData?.account?.profiles.find(
    (profile) => profile.id === accountData.account.selectedProfile
  )
  const myRentalsTotalItems = pathOr(0, ['myRentalHistory', 'totalCurrentRentals'], myRentalsData)
  const helpURLConfig = findConfigValue(HELP_SITE.URL_HELP_SITE, appConfig)
  const feedbackURLConfig = findConfigValue(HELP_SITE.URL_FEEDBACK, appConfig)
  const email = accountData?.account?.email
  const cpId = accountData?.account?.cpCustomerID

  // Handlers
  const onAccountNavClick = useCallback(() => {
    dispatch(toggleAccountNavVisibility())
  }, [dispatch])

  const onLogoutClick = useCallback(() => {
    if (email) {
      segmentTrackSignout({ email })
    }
    dispatch(logoutAndRedirectTo(''))
  }, [dispatch, email])

  const onSideNavLinkClick = useCallback(() => {
    dispatch(toggleNavigationVisibility())
  }, [dispatch])

  const handleClickOutside = useCallback(
    (event) => {
      if (
        event.target.closest('#navigation-account-side') ||
        event.target.closest('#navigation-account-nav-button')
      ) {
        return
      }
      dispatch(setAccountNavVisibility(false))
    },
    [dispatch]
  )

  const onSelectProfile = useCallback(
    (profileToSelect) => {
      if (
        requirePin &&
        hasHigherParentalRating(currentProfile?.maxRating, profileToSelect?.maxRating)
      ) {
        dispatch(switchProfileWithPin(profileToSelect))
        return
      }
      dispatch(switchProfile(profileToSelect))
    },
    [dispatch, requirePin, currentProfile]
  )

  return {
    display,
    isAuthenticated,
    theme,
    navComponentWidth,
    selectedProfile,
    setSelectedProfile,
    requirePin,
    currentProfile,
    myRentalsTotalItems,
    helpURLConfig,
    feedbackURLConfig,
    email,
    cpId,
    onAccountNavClick,
    onLogoutClick,
    onSideNavLinkClick,
    handleClickOutside,
    onSelectProfile,
    accountLoading,
    myRentalsLoading,
    accountData
  }
}
